import React, { useEffect } from 'react';
import { NotificationToastProps, NotificationMessage } from '../types/airflow';

const NotificationToast: React.FC<NotificationToastProps> = ({ notification, onDismiss }) => {
  const { id, type, title, message, timestamp, autoHide = true, duration = 5000 } = notification;

  useEffect(() => {
    if (autoHide) {
      const timer = setTimeout(() => {
        onDismiss(id);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [id, autoHide, duration, onDismiss]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      default:
        return 'ℹ️';
    }
  };

  const getTypeClass = () => {
    switch (type) {
      case 'success':
        return 'notification-success';
      case 'error':
        return 'notification-error';
      case 'warning':
        return 'notification-warning';
      case 'info':
        return 'notification-info';
      default:
        return 'notification-info';
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`notification-toast ${getTypeClass()}`}>
      <div className="notification-content">
        <div className="notification-header">
          <span className="notification-icon">{getIcon()}</span>
          <span className="notification-title">{title}</span>
          <span className="notification-time">{formatTime(timestamp)}</span>
          <button
            className="notification-close-btn"
            onClick={() => onDismiss(id)}
            title="Dismiss"
          >
            ✕
          </button>
        </div>
        {message && (
          <div className="notification-message">
            {message}
          </div>
        )}
      </div>
      {autoHide && (
        <div 
          className="notification-progress" 
          style={{ animationDuration: `${duration}ms` }}
        />
      )}
    </div>
  );
};

// Container component for managing multiple notifications
interface NotificationContainerProps {
  notifications: NotificationMessage[];
  onDismiss: (id: string) => void;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

export const NotificationContainer: React.FC<NotificationContainerProps> = ({
  notifications,
  onDismiss,
  position = 'top-right'
}) => {
  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className={`notification-container ${position}`}>
      {notifications.map((notification) => (
        <NotificationToast
          key={notification.id}
          notification={notification}
          onDismiss={onDismiss}
        />
      ))}
    </div>
  );
};

// Hook for managing notifications
export const useNotifications = () => {
  const [notifications, setNotifications] = React.useState<NotificationMessage[]>([]);

  const addNotification = React.useCallback((
    type: NotificationMessage['type'],
    title: string,
    message?: string,
    options?: {
      autoHide?: boolean;
      duration?: number;
    }
  ) => {
    const notification: NotificationMessage = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      type,
      title,
      message: message || '',
      timestamp: new Date(),
      autoHide: options?.autoHide ?? true,
      duration: options?.duration ?? 5000
    };

    setNotifications(prev => [notification, ...prev]);
    return notification.id;
  }, []);

  const dismissNotification = React.useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const clearAllNotifications = React.useCallback(() => {
    setNotifications([]);
  }, []);

  // Convenience methods
  const showSuccess = React.useCallback((title: string, message?: string, options?: { autoHide?: boolean; duration?: number }) => {
    return addNotification('success', title, message, options);
  }, [addNotification]);

  const showError = React.useCallback((title: string, message?: string, options?: { autoHide?: boolean; duration?: number }) => {
    return addNotification('error', title, message, { ...options, autoHide: options?.autoHide ?? false });
  }, [addNotification]);

  const showWarning = React.useCallback((title: string, message?: string, options?: { autoHide?: boolean; duration?: number }) => {
    return addNotification('warning', title, message, options);
  }, [addNotification]);

  const showInfo = React.useCallback((title: string, message?: string, options?: { autoHide?: boolean; duration?: number }) => {
    return addNotification('info', title, message, options);
  }, [addNotification]);

  return {
    notifications,
    addNotification,
    dismissNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };
};

export default NotificationToast;